import { useState } from "react"
import styled, { css } from "styled-components"
import mapCouronneOuest from "@assets/img/map-couronne-ouest.png"

const Section = styled.section`
  width: 100%;
  padding: clamp(3.5rem, 7vw, 5.5rem) 1.5rem;
  background: radial-gradient(circle at 10% 15%, rgba(27, 117, 188, 0.12), transparent 60%), linear-gradient(180deg, #f5f8fb 0%, #ffffff 60%);
  color: ${({ theme }) => theme.textPrimary};
`

const Content = styled.div`
  width: min(1200px, 92vw);
  margin: 0 auto;
  display: grid;
  gap: clamp(2.5rem, 5vw, 4rem);
`

const Header = styled.div`
  display: grid;
  gap: 0.8rem;
  max-width: 720px;
`

const Eyebrow = styled.span`
  font-size: 0.85rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.primary};
  font-weight: 600;
`

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 4vw, 2.8rem);
  color: ${({ theme }) => theme.primaryDark};
`

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.textSecondary || theme.textPrimary};
`

const CoverageGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 1200px) {
    grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  }
`

const MapCard = styled.div`
  background: linear-gradient(135deg, #0a1f35 0%, #102845 100%);
  border-radius: 2px;
  padding: 1.5rem;
  color: #ffffff;
  box-shadow: 0 24px 60px rgba(8, 21, 38, 0.2);
  display: grid;
  gap: 1rem;
`

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const MapTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.8);
`

const MapBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
`

const MapCanvas = styled.div<{ $image: string }>`
  position: relative;
  border-radius: 2px;
  aspect-ratio: 2224 / 1208;
  background: #0b1d31;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow:
    inset 0 0 50px rgba(5, 16, 28, 0.55),
    0 24px 50px rgba(5, 16, 28, 0.35);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url(${({ $image }) => $image});
    background-size: 100% 100%;
    background-position: center;
    filter: saturate(1.1) contrast(1.05);
    opacity: 0.88;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(7, 19, 33, 0.15), rgba(7, 19, 33, 0.6)),
      radial-gradient(circle at 30% 30%, rgba(99, 195, 255, 0.2), transparent 60%),
      repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.1) 0, rgba(255, 255, 255, 0.1) 1px, transparent 1px, transparent 14px),
      repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0, rgba(255, 255, 255, 0.1) 1px, transparent 1px, transparent 14px);
    opacity: 0.55;
    pointer-events: none;
  }
`

const MapPulse = styled.div<{ $active?: boolean }>`
  position: absolute;
  inset: 12%;
  border-radius: 50%;
  border: 1px dashed rgba(99, 195, 255, 0.35);
  background: radial-gradient(circle, rgba(99, 195, 255, 0.22) 0%, rgba(10, 24, 40, 0) 68%);
  pointer-events: none;
  z-index: 1;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  opacity: ${({ $active }) => ($active ? 0.95 : 0.6)};
  transform: scale(${({ $active }) => ($active ? 1.04 : 1)});
`

const MapPoint = styled.button<{ $top: string; $left: string; $active?: boolean }>`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #63c3ff 0%, #63ffca 100%);
  box-shadow:
    0 0 0 6px rgba(99, 195, 255, 0.18),
    0 8px 18px rgba(6, 18, 32, 0.55);
  z-index: 2;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 1px solid rgba(99, 195, 255, 0.45);
    opacity: 0.75;
  }

  &::after {
    content: attr(data-label);
    position: absolute;
    left: 50%;
    bottom: 135%;
    transform: translate(-50%, 10px);
    background: rgba(6, 16, 28, 0.85);
    color: #ffffff;
    padding: 0.35rem 0.6rem;
    border-radius: 2px;
    font-size: 0.72rem;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  ${({ $active }) =>
    $active &&
    css`
      transform: translate(-50%, -50%) scale(1.12);
      box-shadow:
        0 0 0 8px rgba(99, 195, 255, 0.22),
        0 12px 26px rgba(6, 18, 32, 0.6);

      &::after {
        opacity: 1;
        transform: translate(-50%, 0);
      }

      &::before {
        opacity: 1;
      }
    `}
`

const MapHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
`

const CityGrid = styled.div`
  display: grid;
  gap: 0.6rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const CityToggle = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 2px;
  font-size: 0.92rem;
  line-height: 1.2;
  text-align: center;
  background: rgba(27, 117, 188, 0.08);
  color: ${({ theme }) => theme.primaryDark};
  border: 1px solid rgba(27, 117, 188, 0.2);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    background: rgba(27, 117, 188, 0.16);
    box-shadow: 0 10px 24px rgba(27, 117, 188, 0.18);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(27, 117, 188, 0.22);
      border-color: rgba(27, 117, 188, 0.4);
      box-shadow: 0 12px 26px rgba(27, 117, 188, 0.22);
      transform: translateY(-1px);
    `}
`

const coverageCities = [
  { label: "Donnacona", point: "Donnacona" },
  { label: "Pont-Rouge", point: "Pont-Rouge" },
  { label: "Neuville", point: "Neuville" },
  { label: "Portneuf", point: "Portneuf" },
  { label: "Cap-Santé", point: "Cap-Santé" },
  { label: "Saint-Augustin-de-Desmaures", point: "Saint-Augustin-de-Desmaures" },
  { label: "Cap-Rouge", point: "Cap-Rouge" },
  { label: "Sainte-Foy", point: "Sainte-Foy" },
  { label: "Ancienne-Lorette", point: "Ancienne-Lorette" },
  { label: "Couronne ouest de Québec", point: null },
  { label: "Et environs", point: null },
]

const mapPoints = [
  { label: "Portneuf", top: "82%", left: "16%" },
  { label: "Cap-Santé", top: "92%", left: "30%" },
  { label: "Donnacona", top: "90%", left: "38%" },
  { label: "Pont-Rouge", top: "59%", left: "44%" },
  { label: "Neuville", top: "82.5%", left: "59.5%" },
  { label: "Saint-Augustin-de-Desmaures", top: "63%", left: "78%" },
  { label: "Cap-Rouge", top: "56%", left: "93.5%" },
  { label: "Sainte-Foy", top: "65%", left: "90%" },
  { label: "Ancienne-Lorette", top: "40%", left: "94%" },
]

const CoverageSection = () => {
  const [hoverCity, setHoverCity] = useState<string | null>(null)
  const [pinnedCity, setPinnedCity] = useState<string | null>(null)
  const activeCity = hoverCity ?? pinnedCity
  const regionActive = activeCity === "Et environs" || activeCity === "Couronne ouest de Québec"

  const togglePinnedCity = (label: string) => {
    setPinnedCity((prev) => (prev === label ? null : label))
  }

  return (
    <Section>
      <Content>
        <Header>
          <Eyebrow>Territoire desservi</Eyebrow>
          <Title>Portneuf, la couronne ouest de Québec et les secteurs voisins.</Title>
          <Subtitle>
            Une présence locale pour coordonner les demandes, les urgences et les fournisseurs qui interviennent dans vos immeubles.
          </Subtitle>
        </Header>
        <CoverageGrid>
          <MapCard>
            <MapHeader>
              <MapTitle>Carte de couverture</MapTitle>
              <MapBadge>Portneuf + Québec Ouest</MapBadge>
            </MapHeader>
            <MapCanvas $image={mapCouronneOuest} aria-label="Carte de couverture Portneuf et Québec Ouest">
              <MapPulse $active={regionActive} />
              {mapPoints.map((point) => (
                <MapPoint
                  key={point.label}
                  type="button"
                  $top={point.top}
                  $left={point.left}
                  $active={activeCity === point.label}
                  data-label={point.label}
                  aria-label={point.label}
                  aria-pressed={pinnedCity === point.label}
                  onMouseEnter={() => setHoverCity(point.label)}
                  onMouseLeave={() => setHoverCity(null)}
                  onFocus={() => setHoverCity(point.label)}
                  onBlur={() => setHoverCity(null)}
                  onClick={() => togglePinnedCity(point.label)}
                />
              ))}
            </MapCanvas>
            <MapHint>Sélectionnez une ville pour la repérer sur la carte.</MapHint>
          </MapCard>
          <CityGrid>
            {coverageCities.map((city) => {
              const targetLabel = city.point || city.label
              const isActive = activeCity === targetLabel
              return (
                <CityToggle
                  key={city.label}
                  type="button"
                  $active={isActive}
                  aria-pressed={pinnedCity === targetLabel}
                  onMouseEnter={() => setHoverCity(targetLabel)}
                  onMouseLeave={() => setHoverCity(null)}
                  onFocus={() => setHoverCity(targetLabel)}
                  onBlur={() => setHoverCity(null)}
                  onClick={() => togglePinnedCity(targetLabel)}
                >
                  {city.label}
                </CityToggle>
              )
            })}
          </CityGrid>
        </CoverageGrid>
      </Content>
    </Section>
  )
}

export default CoverageSection
